<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import {
  searchContacts,
  createNewContact,
  fetchContactableInboxes,
  processContactableInboxes,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import wootAPI from '@/v3/api/apiClient';

import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';

const props = defineProps({
  alignPosition: {
    type: String,
    default: 'left',
  },
  contactId: {
    type: String,
    default: null,
  },
  enforceAgentVisibility: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const showComposeNewConversation = ref(false);

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const currentRole = useMapGetter('getCurrentRole');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledOnAccount = useMapGetter('accounts/isFeatureEnabledonAccount');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const composePopoverClass = computed(() => {
  if (props.alignPosition === 'right') {
    return 'absolute left-0';
  } else if (props.alignPosition === 'center') {
    return ''; // No positioning classes - handled by wrapper
  } else {
    return 'absolute right-0';
  }
});

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      contacts.value = await searchContacts(query);
      isSearching.value = false;
    } catch (error) {
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    } finally {
      isSearching.value = false;
    }
  },
  300,
  false
);

const resetContacts = () => {
  contacts.value = [];
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      selectedContact.value.contactInboxes = contactableInboxes;
      isFetchingInboxes.value = false;
    } catch (error) {
      isFetchingInboxes.value = false;
    }
  }
};

const checkConversationPermission = async (contactId, inboxId) => {
  if (!contactId || !inboxId) return { can_send: true };

  try {
    const headers = getAuthHeaders();
    const response = await wootAPI.get(
      `/api/v1/accounts/${currentAccountId.value}/contacts/${contactId}/conversation_check/${inboxId}`,
      { headers }
    );

    return response.data;
  } catch (error) {
    console.error('Error checking conversation permission:', error);
    return { can_send: true }; // Default to allowing in case of error
  }
};

const handleTargetInbox = async inbox => {
  targetInbox.value = inbox;
  resetContacts();

  // Check permission when inbox is selected
  if (
    props.enforceAgentVisibility &&
    selectedContact.value?.id &&
    inbox?.id
  ) {
    const permissionCheck = await checkConversationPermission(
      selectedContact.value.id,
      inbox.id
    );

    if (permissionCheck.can_send === false) {
      const agentName = permissionCheck.assigned_agent?.name || 'outro agente';
      useAlert(
        `Esta conversa está atribuída a ${agentName}.`,
        { type: 'warning' }
      );
      targetInbox.value = null;
      return;
    }

    if (permissionCheck.force_new_conversation) {
      useAlert(
        'Uma nova conversa será criada pois já existe uma conversa atribuída a outro agente nesta inbox.',
        { type: 'info' }
      );
    }
  }
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
};

const closeCompose = () => {
  showComposeNewConversation.value = false;
  selectedContact.value = null;
  targetInbox.value = null;
  resetContacts();
};

const getCookie = name => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      'client': sessionInfo['client'],
      'uid': sessionInfo['uid']
    };
  } catch (e) {
    console.error('Error parsing cookie:', e);
    return {};
  }
};

const fetchAgentInfo = async (inboxId) => {
  if (!inboxId) return false;

  try {
    const headers = getAuthHeaders();
    const response = await wootAPI.get(
      '/api/v1/inboxmeta/fetch_agentis',
      {
        params: { inbox_id: inboxId },
        headers
      }
    );

    console.log('Full API response:', response.data);

    // Check if response.data.agent exists and is not null/empty
    const hasAgent = response.data && response.data.agent && response.data.agent.trim() !== '';

    console.log('Fetched AI agent info:', {
      hasAgent,
      agentName: response.data?.agent,
      rawResponse: response.data
    });

    return hasAgent;
  } catch (error) {
    console.error('Error fetching agent info:', error);
    return false;
  }
};

const addAtendimentoLabelToNewConversation = async (conversationId, inboxId) => {
  try {
    const hasIaAgent = await fetchAgentInfo(inboxId);

    console.log('hasIaAgent:', hasIaAgent);

    if (hasIaAgent) {
      // Add the "atendimento-humano" label to the new conversation
      await store.dispatch('conversationLabels/update', {
        conversationId,
        labels: ['atendimento-humano'],
      });

      console.log('Added atendimento-humano label to conversation:', conversationId);
    }
  } catch (error) {
    console.error('Error adding atendimento-humano label:', error);
    // Don't throw - we don't want label failures to break the conversation creation flow
  }
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    // Add message source tracking to the payload
    const enrichedPayload = {
      ...payload,
      custom_attributes: {
        ...payload.custom_attributes,
        message_source: props.messageSource,
        sent_from_page: window.location.pathname,
      }
    };

    // Check permission one more time before creating conversation
    if (
      props.enforceAgentVisibility &&
      selectedContact.value?.id &&
      targetInbox.value?.id
    ) {
      const permissionCheck = await checkConversationPermission(
        selectedContact.value.id,
        targetInbox.value.id
      );

      if (permissionCheck.can_send === false) {
        useAlert(
          'Você não tem permissão para enviar mensagens nesta conversa.',
          { type: 'error' }
        );
        return false;
      }

      // If force new conversation, add a flag to the payload
      if (permissionCheck.force_new_conversation) {
        enrichedPayload.custom_attributes.forced_new_conversation = true;
        enrichedPayload.custom_attributes.original_assigned_to = permissionCheck.assigned_agent?.id;
      }
    }

    const data = await store.dispatch('contactConversations/create', {
      params: enrichedPayload,
      isFromWhatsApp,
    });

    // Log metrics
    console.log('Conversation created with metrics:', {
      messageSource: props.messageSource,
      contactId: selectedContact.value?.id,
      inboxId: targetInbox.value?.id,
      conversationId: data.id,
      sentFromPage: window.location.pathname,
    });

    // Add "atendimento-humano" label if inbox has AI agent
    if (targetInbox.value?.id) {
      await addAtendimentoLabelToNewConversation(data.id, targetInbox.value.id);
    }

    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
    };
    closeCompose();
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
    return true; // Return success
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false; // Return failure
  }
};

const toggle = () => {
  showComposeNewConversation.value = !showComposeNewConversation.value;
};

watch(
  activeContact,
  () => {
    if (activeContact.value && props.contactId) {
      const contactInboxes = activeContact.value?.contactInboxes || [];
      selectedContact.value = {
        ...activeContact.value,
        contactInboxes: processContactableInboxes(contactInboxes),
      };
    }
  },
  { immediate: true, deep: true }
);

const handleClickOutside = () => {
  showComposeNewConversation.value = false;
};

onMounted(() => resetContacts());

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showComposeNewConversation.value) {
        showComposeNewConversation.value = false;
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    v-on-click-outside="[
      handleClickOutside,
      // Fixed and edge case https://github.com/chatwoot/chatwoot/issues/10785
      // This will prevent closing the compose conversation modal when the editor Create link popup is open.
      { ignore: ['div.ProseMirror-prompt'] },
    ]"
    class="relative"
    :class="{
      'z-40': showComposeNewConversation,
    }"
  >
    <slot
      name="trigger"
      :is-open="showComposeNewConversation"
      :toggle="toggle"
    />
    <!-- Backdrop -->
    <div
      v-if="showComposeNewConversation && alignPosition === 'center'"
      class="fixed inset-0 bg-n-alpha-black1 z-40"
      @click="closeCompose"
    />
    <!-- Center mode: wrap in positioning div -->
    <div
      v-if="showComposeNewConversation && alignPosition === 'center'"
      class="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50"
    >
      <ComposeNewConversationForm
        :contacts="contacts"
        :contact-id="contactId"
        :is-loading="isSearching"
        :current-user="currentUser"
        :selected-contact="selectedContact"
        :target-inbox="targetInbox"
        :is-creating-contact="isCreatingContact"
        :is-fetching-inboxes="isFetchingInboxes"
        :is-direct-uploads-enabled="directUploadsEnabled"
        :contact-conversations-ui-flags="uiFlags"
        :contacts-ui-flags="contactsUiFlags"
        :message-signature="messageSignature"
        :send-with-signature="sendWithSignature"
        @search-contacts="onContactSearch"
        @reset-contact-search="resetContacts"
        @update-selected-contact="handleSelectedContact"
        @update-target-inbox="handleTargetInbox"
        @clear-selected-contact="clearSelectedContact"
        @create-conversation="createConversation"
        @discard="closeCompose"
      />
    </div>
    <!-- Non-center mode: render directly with positioning -->
    <ComposeNewConversationForm
      v-else-if="showComposeNewConversation"
      :contacts="contacts"
      :contact-id="contactId"
      :is-loading="isSearching"
      :current-user="currentUser"
      :selected-contact="selectedContact"
      :target-inbox="targetInbox"
      :is-creating-contact="isCreatingContact"
      :is-fetching-inboxes="isFetchingInboxes"
      :is-direct-uploads-enabled="directUploadsEnabled"
      :contact-conversations-ui-flags="uiFlags"
      :contacts-ui-flags="contactsUiFlags"
      :class="composePopoverClass"
      :message-signature="messageSignature"
      :send-with-signature="sendWithSignature"
      @search-contacts="onContactSearch"
      @reset-contact-search="resetContacts"
      @update-selected-contact="handleSelectedContact"
      @update-target-inbox="handleTargetInbox"
      @clear-selected-contact="clearSelectedContact"
      @create-conversation="createConversation"
      @discard="closeCompose"
    />
  </div>
</template>
